import React, { useState } from 'react';
import './LegalResultsDisplay.css'; // You can reuse ResultsDisplay.css
import { useApi } from "../../api/useApi";
import { normalizeApiUrl } from "../../api/apiClient";
import { getIncidentApiToken } from '../../api/tokenService';
import PerspectivesPanel from './PerspectivesPanel';

// Helper to safely render arrays or strings
const renderList = (items) => {
  if (!items) return 'Not available';
  if (Array.isArray(items)) {
    return items.map((item, idx) => <li key={idx}>{item}</li>);
  }
  return items;
};

const LegalResultsDisplay = ({ result, apiUrl, caseData }) => {
    const [activeTab, setActiveTab] = useState('assessment');
    const [exporting, setExporting] = useState(false);
    const [sending, setSending] = useState(false);
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [emailAddresses, setEmailAddresses] = useState('');
     const { postWithIdToken } = useApi();

  if (!result) {
    return (
      <div className="no-result">
        <h3>No analysis result available</h3>
        <p>Submit a case to see the assessment</p>
      </div>
    );
  }

  // --- Export PDF ---
  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const idToken = await getIncidentApiToken();
      const response = await postWithIdToken(`${apiUrl}/api/v1/export/pdf`, {
    analysisResult: result,
    options: {  }
  }, idToken);
      if (response.success) {
        const downloadResponse = await fetch(normalizeApiUrl(`${apiUrl}${response.data.downloadUrl}`), {
          headers: { Authorization: `Bearer ${idToken}` }
        });
        if (!downloadResponse.ok) throw new Error('PDF download failed');
        const blobUrl = URL.createObjectURL(await downloadResponse.blob());
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = response.data.fileName || 'legal-analysis.pdf';
        link.click();
        URL.revokeObjectURL(blobUrl);
      } else {
        alert('Export failed: ' + response.data.error);
      }
    } catch (error) {
      console.error('Export error:', error);
      alert('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  // --- Send Email ---
  const handleSendEmail = () => {
    setShowEmailModal(true);
  };

  const submitEmail = async () => {
    if (!emailAddresses.trim()) {
      alert('Please enter at least one email address');
      return;
    }
    setSending(true);
    setShowEmailModal(false);
    try {
      const idToken = await getIncidentApiToken();
      const response = await postWithIdToken(`${apiUrl}/api/v1/notify/email`, {
            to: emailAddresses.split(',').map(e => e.trim()),
            analysisResult: result,
            includePdf: true,
            }, idToken);
      
      if (response.success) {
        alert('Email sent successfully!');
      } else {
        alert('Email sending failed: ' + response.data.error);
      }
    } catch (error) {
      console.error('Email error:', error);
      alert('Email sending failed. Please try again.');
    } finally {
      setSending(false);
      setEmailAddresses('');
    }
  };

  // Simple modal for email input
const EmailModal = () => (
  <div className="modal-overlay" onClick={() => setShowEmailModal(false)}>
    <div className="modal-card" onClick={(e) => e.stopPropagation()}>
      <div className="modal-header">
        <h3>Send via Email</h3>
        <button className="modal-close" onClick={() => setShowEmailModal(false)}>
          <svg viewBox="0 0 24 24" width="24" height="24">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
          </svg>
        </button>
      </div>
      <div className="modal-body">
        <label>Email addresses</label>
        <input
          type="text"
          value={emailAddresses}
          onChange={(e) => setEmailAddresses(e.target.value)}
          placeholder="e.g. user@example.com, colleague@company.com"
          autoFocus
        />
        <p className="input-hint">Separate multiple addresses with commas</p>
      </div>
      <div className="modal-footer">
        <button className="btn btn-secondary" onClick={() => setShowEmailModal(false)}>
          Cancel
        </button>
        <button className="btn btn-primary" onClick={submitEmail} disabled={sending}>
          {sending ? 'Sending...' : 'Send Report'}
        </button>
      </div>
    </div>
  </div>
);

  return (
    <div className="results-display legal-results">
      {/* Header */}
      <div className="results-header">
        <h2>Case Assessment Results</h2>
        <div className="result-meta">
          <span className="case-id">Case ID: {result.caseId || 'Unknown'}</span>
          <span className="processing-time">
            <span>⏱️</span> {result.metadata?.processingTime || 'Unknown'}
          </span>
          {result.metadata?.modelUsed && (
            <span className="ai-badge">{result.metadata.modelUsed}</span>
          )}
        </div>
      </div>

      <div className="results-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'assessment'}
          className={`results-tab ${activeTab === 'assessment' ? 'active' : ''}`}
          onClick={() => setActiveTab('assessment')}
        >
          📄 Assessment
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'perspectives'}
          className={`results-tab ${activeTab === 'perspectives' ? 'active' : ''}`}
          onClick={() => setActiveTab('perspectives')}
        >
          👁️ Perspectives
        </button>
      </div>

      <div
        className="tab-panel assessment-panel"
        style={{ display: activeTab === 'assessment' ? 'flex' : 'none' }}
      >
      <div className="assessment-actions">
        <button
          onClick={handleExportPDF}
          disabled={exporting}
          className="btn btn-export"
        >
          {exporting ? 'Exporting...' : '📄 Export PDF'}
        </button>
        <button
          onClick={handleSendEmail}
          disabled={sending}
          className="btn btn-email"
        >
          {sending ? 'Sending...' : '✉️ Send Email'}
        </button>
      </div>

      {/* 1. Summary */}
      <div className="result-section">
        <h3>📄 Case Summary</h3>
        <div className="summary-box">
          <p>{result.summary || 'No summary provided.'}</p>
        </div>
      </div>

      {/* 2. Relevant Laws and Sections */}
      <div className="result-section">
        <h3>⚖️ Relevant Laws & Sections</h3>
        <div className="laws-list">
          {result.relevantLaws && Array.isArray(result.relevantLaws) ? (
            <ul>
              {result.relevantLaws.map((law, index) => (
                <li key={index}>
                  <strong>{law.section || law}</strong>
                  {law.description && <p className="law-description">{law.description}</p>}
                </li>
              ))}
            </ul>
          ) : typeof result.relevantLaws === 'string' ? (
            <p>{result.relevantLaws}</p>
          ) : (
            <p>No relevant laws identified.</p>
          )}
        </div>
      </div>

      {/* 3. Suggested Next Steps */}
      <div className="result-section">
        <h3>🔜 Suggested Next Steps</h3>
        <div className="next-steps">
          {result.nextSteps && Array.isArray(result.nextSteps) ? (
            <ol>
              {result.nextSteps.map((step, index) => (
                <li key={index}>{step}</li>
              ))}
            </ol>
          ) : typeof result.nextSteps === 'string' ? (
            <p>{result.nextSteps}</p>
          ) : (
            <p>No next steps suggested.</p>
          )}
        </div>
      </div>

      {/* 4. Plain-Language Explanations */}
      <div className="result-section">
        <h3>🗣️ Plain-Language Explanations</h3>
        <div className="explanations">
          {result.plainLanguageExplanations && Array.isArray(result.plainLanguageExplanations) ? (
            <ul>
              {result.plainLanguageExplanations.map((exp, index) => (
                <li key={index}>
                  <strong>{exp.term}:</strong> {exp.explanation}
                </li>
              ))}
            </ul>
          ) : typeof result.plainLanguageExplanations === 'string' ? (
            <p>{result.plainLanguageExplanations}</p>
          ) : (
            <p>No explanations provided.</p>
          )}
        </div>
      </div>

      {/* Additional Metadata (optional) */}
      {result.metadata && (
        <div className="metadata">
          <h4>Analysis Metadata</h4>
          <div className="metadata-grid">
            <div className="metadata-item">
              <span className="metadata-label">Jurisdiction:</span>
              <span className="metadata-value">{result.metadata.jurisdiction || 'India'}</span>
            </div>
            <div className="metadata-item">
              <span className="metadata-label">Case Type:</span>
              <span className="metadata-value">{result.metadata.caseType || 'Criminal'}</span>
            </div>
            <div className="metadata-item">
              <span className="metadata-label">Analysis Depth:</span>
              <span className="metadata-value">{result.metadata.analysisDepth || 'standard'}</span>
            </div>
            {result.metadata.tokensUsed && (
              <div className="metadata-item">
                <span className="metadata-label">Tokens Used:</span>
                <span className="metadata-value">{result.metadata.tokensUsed}</span>
              </div>
            )}
          </div>
        </div>
      )}

      </div>
      <div
        className="tab-panel perspectives-panel-wrap"
        style={{ display: activeTab === 'perspectives' ? 'flex' : 'none' }}
      >
        <PerspectivesPanel caseData={caseData} apiUrl={apiUrl} />
      </div>

       {/* Email modal */}
      {showEmailModal && <EmailModal />}
    </div>
  );
};



export default LegalResultsDisplay;